import { Ban, CreditCard, Download, RefreshCw } from 'lucide-react'

import {
  Button,
  Card,
  CardBody,
  ConfirmNameModal,
  DataTable,
  EmptyState,
  Figures,
  Icon,
  IconButton,
} from 'ui'
import {
  formatCurrency,
  formatDate,
  formatMonthDay,
  formatYear,
  titleize,
} from 'utils/billing'

const CONTACT_EMAIL = 'accountmanagement@expandrive.com'
const CONTACT_PHONE = '+1 (510) 500-0245'

const SummaryList = ({ items }) => (
  <dl className="tw:grid tw:gap-3">
    {items.filter(([, value]) => value !== undefined).map(([label, value]) => (
      <div className="tw:min-w-0" key={label}>
        <dt className="ds-helper">{label}</dt>
        <dd className="tw:mt-1 tw:[overflow-wrap:anywhere] tw:text-base tw:text-foreground">{value ?? 'None'}</dd>
      </div>
    ))}
  </dl>
)

const hasPaymentCard = summary => Boolean(
  summary.paymentMethod?.last4 || summary.paymentMethod?.cardNumber,
)

const BillingContactInformation = ({ brand = {} }) => {
  const brandName = brand.name || 'ExpanDrive'
  const brandPhone = brand.phone || CONTACT_PHONE

  return (
    <p className="ds-helper tw:mt-6 tw:border-t tw:border-default tw:pt-4 tw:text-center">
      Please contact {brandName} to make changes to your account. You can reach us by telephone at{' '}
      <a className="tw:underline" href={`tel:${brandPhone.replace(/[^+\d]/g, '')}`}>{brandPhone}</a>
      {' '}or email <a className="tw:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
    </p>
  )
}

const BillingFigures = ({ summary }) => {
  const balance = summary.balance || {}
  const subscription = summary.subscription || {}
  const hasCard = hasPaymentCard(summary)
  const renewalCanceled = subscription.cancelAtRenewal === true
  const renewalYear = formatYear(subscription.expirationDate)
  const nextCharge = !hasCard
    ? { label: 'No Card on File', value: '—' }
    : renewalCanceled
      ? { label: 'No Further Charges', value: '—' }
      : {
        label: renewalYear ? `Next Charge, ${renewalYear}` : 'Next Charge Date Unavailable',
        value: formatMonthDay(subscription.expirationDate),
      }

  return (
    <Figures
      accentFirst={false}
      className="tw:mb-10"
      items={[
        { label: 'Current Balance', value: formatCurrency(balance.amount ?? 0, balance.currency) },
        {
          label: `${titleize(subscription.period)} Rate, Paid in Advance`,
          value: formatCurrency(subscription.rate, balance.currency),
        },
        nextCharge,
      ]}
    />
  )
}

const BillingSummaryCard = ({ action, children, title }) => (
  <Card className="tw:h-full tw:min-h-[280px]">
    <CardBody className="tw:flex tw:h-full tw:flex-col">
      <h2 className="ds-heading-4 tw:mb-8">{title}</h2>
      {children}
      {action && <div className="tw:mt-auto tw:flex tw:justify-end tw:pt-5">{action}</div>}
    </CardBody>
  </Card>
)

const BillingSummaryCards = ({ isSubmitting, onOpenPaymentForm, onSetAutoRenewal, summary }) => {
  const balance = summary.balance || {}
  const subscription = summary.subscription || {}
  const paymentMethod = summary.paymentMethod || {}
  const hasCard = hasPaymentCard(summary)
  const renewalCanceled = subscription.cancelAtRenewal === true
  const renewalDate = subscription.expirationDate ? formatDate(subscription.expirationDate) : 'the end of your current term'

  return (
    <div className="tw:grid tw:grid-cols-1 tw:gap-6 tw:lg:grid-cols-3">
      <BillingSummaryCard title="Subscription">
        <SummaryList
          items={[
            ['Period', titleize(subscription.period)],
            ['Rate', formatCurrency(subscription.rate, balance.currency)],
            ['Site', subscription.siteName],
          ]}
        />
      </BillingSummaryCard>

      <BillingSummaryCard
        action={(
          <Button
            icon={<Icon as={CreditCard} />}
            onClick={onOpenPaymentForm}
            type="button"
            variant={hasCard ? 'secondary' : 'primary'}
          >
            {hasCard ? 'Change Credit Card' : 'Pay via Credit Card'}
          </Button>
        )}
        title="Payment Method"
      >
        {hasCard ? (
          <SummaryList
            items={[
              ['Type', paymentMethod.type || 'Credit card'],
              ['Card', [paymentMethod.cardType, paymentMethod.last4 || paymentMethod.cardNumber].filter(Boolean).join(' ')],
              ['Billing Email', paymentMethod.billingEmail || summary.account?.email],
            ]}
          />
        ) : (
          <p className="ds-helper">No payment method on file.</p>
        )}
      </BillingSummaryCard>

      <BillingSummaryCard
        action={renewalCanceled ? (
          <Button
            disabled={isSubmitting}
            icon={<Icon as={RefreshCw} />}
            onClick={() => onSetAutoRenewal(true)}
            type="button"
          >
            Resume Renewal
          </Button>
        ) : (
          <ConfirmNameModal
            actionLabel="Cancel at Renewal"
            actionPendingLabel="Canceling…"
            isPending={isSubmitting}
            onConfirm={() => onSetAutoRenewal(false)}
            requireNameConfirmation={false}
            title="Cancel at Renewal?"
            trigger={(
              <Button
                disabled={isSubmitting}
                icon={<Icon as={Ban} />}
                type="button"
                variant="danger"
              >
                Cancel at Renewal
              </Button>
            )}
          >
            Your subscription stays active until {renewalDate}, then it will not renew and seats lose access.
          </ConfirmNameModal>
        )}
        title="Renewal"
      >
        <SummaryList
          items={[
            ['Auto-Renewal', renewalCanceled ? `Cancels ${renewalDate}` : 'Enabled'],
          ]}
        />
      </BillingSummaryCard>
    </div>
  )
}

const BillingHistoryTable = ({ brand, history, isDownloading, onDownloadInvoice }) => {
  const columns = [
    {
      cellClassName: 'tw:text-foreground-muted',
      key: 'date',
      label: 'Date',
      render: item => formatDate(item.createdAt),
      width: '18%',
    },
    {
      key: 'item',
      label: 'Item',
      primary: true,
      render: item => (
        <div>
          <div>{item.description}{item.reversed ? ' (Reversed)' : ''}</div>
          {item.detail && <div className="ds-helper tw:mt-1">{item.detail}</div>}
        </div>
      ),
      width: '43%',
    },
    {
      cellClassName: 'tw:text-right',
      key: 'amount',
      label: 'Amount',
      render: item => (
        <span className={item.type === 'payment' && !item.reversed ? 'tw:font-semibold tw:text-status-success-foreground' : 'tw:font-semibold tw:text-foreground-strong'}>
          {item.reversed ? <s>{formatCurrency(item.amount, item.currency)}</s> : formatCurrency(item.amount, item.currency)}
        </span>
      ),
      width: '17%',
    },
    {
      cellClassName: 'tw:text-right',
      key: 'balance',
      label: 'Balance',
      render: item => (
        <span className={Number(item.balance) === 0 ? 'tw:text-foreground-subtle' : 'tw:text-foreground-strong'}>
          {formatCurrency(item.balance, item.currency)}
        </span>
      ),
      width: '17%',
    },
  ]

  return (
    <section className="tw:mt-10">
      <h2 className="ds-heading-4">Invoice and Payment History</h2>
      <p className="ds-helper tw:mt-1">Download past invoices and payment records.</p>
      <div className="tw:mt-5">
        <DataTable
          actions={item => item.type === 'invoice' && item.hasPdf ? (
            <IconButton
              disabled={isDownloading}
              label={`Download ${item.description} PDF`}
              onClick={() => onDownloadInvoice(item)}
              title="Download PDF"
            >
              <Icon as={Download} />
            </IconButton>
          ) : null}
          columns={columns}
          empty={(
            <EmptyState icon={<Icon as={Download} size={32} />} title="No Invoice or Payment History">
              Records appear here after your first charge.
            </EmptyState>
          )}
          getRowKey={item => `${item.type}-${item.id}`}
          rows={history}
        />
      </div>
      <BillingContactInformation brand={brand} />
    </section>
  )
}

export const BillingDashboard = ({
  isDownloading,
  isSubmitting,
  onDownloadInvoice,
  onOpenPaymentForm,
  onSetAutoRenewal,
  summary,
}) => (
  <>
    <BillingFigures summary={summary} />
    <BillingSummaryCards
      isSubmitting={isSubmitting}
      onOpenPaymentForm={onOpenPaymentForm}
      onSetAutoRenewal={onSetAutoRenewal}
      summary={summary}
    />
    <BillingHistoryTable
      brand={summary.brand}
      history={summary.history || []}
      isDownloading={isDownloading}
      onDownloadInvoice={onDownloadInvoice}
    />
  </>
)

export default BillingDashboard
